import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Country, Currency, Response, SelectItemProps } from '@ui/src';
import { PermissionEnum } from '../../hoc/user-roles/types';
import { administrationDictionariesApi, PermissionResponse } from './service';
import { Role } from '../../hoc/settings/profile/types';
import { Account } from '../../hoc/settings/types';

export interface AdministrationDictionariesState {
  roles?: SelectItemProps[];
  defaultRoles?: Role[];
  users?: SelectItemProps[];
  activeUsers?: SelectItemProps[];
  countries?: SelectItemProps[];
  activePermissions?: PermissionEnum[];
  allPermissions?: PermissionEnum[];
  cardsEnabled?: boolean;
  activeCurrencies?: Currency[];
  accountsList?: Account[];

  whitelabel?: {
    data: {
      value: Record<string, string>;
    };
  };
}

const initialState: AdministrationDictionariesState = {};

export const administrationDictionariesSlice = createSlice({
  name: 'administrationDictionaries',
  initialState,
  reducers: {
    setActivePermissions: (state, action: PayloadAction<PermissionEnum[]>) => {
      state.activePermissions = action.payload;
    },
    setAdministrationCountries: (state, action: PayloadAction<Country[]>) => {
      state.countries =
        action.payload?.map((item) => ({
          name: item.name,
          value: item.id,
        })) || [];
    },
    setAdministrationCurrencies: (state, action: PayloadAction<Currency[]>) => {
      state.activeCurrencies = action?.payload?.map((item) => item);
    },
    setCardsEnabled: (state, action: PayloadAction<boolean>) => {
      state.cardsEnabled = action.payload;

      localStorage.setItem(
        'cardsEnabled',
        JSON.stringify({ cardsEnabled: action.payload }),
      );
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      administrationDictionariesApi.endpoints.getEmployeesBaseDictionaries
        .matchFulfilled,
      (
        state,
        action: PayloadAction<{
          roles: Response<{
            name: string;
            id: string;
            active: boolean;
            permissions: PermissionEnum[];
            notificationTypeTemplateId?: string;
          }>;
          allPermissions: { data: PermissionEnum[] };
          accounts: Response<Account>;
        }>,
      ) => {
        state.roles =
          action.payload?.roles?.data?.edges
            ?.filter((item) => item.node.active)
            .map((item) => ({
              name: item.node.name,
              value: item.node.id,
              suffix: item.node.notificationTypeTemplateId,
            })) || [];

        state.defaultRoles = action.payload.roles.data.edges?.map(
          (item) => item.node,
        );

        state.accountsList = action.payload?.accounts?.data?.edges?.map(
          (item) => item.node,
        );

        state.allPermissions = action.payload.allPermissions.data;

        const cardsEnabled = localStorage.getItem('cardsEnabled');

        if (cardsEnabled) {
          const parsed = JSON.parse(cardsEnabled);

          state.cardsEnabled = parsed.cardsEnabled;
        }
      },
    );
    builder.addMatcher(
      administrationDictionariesApi.endpoints.getAdministrationCountries
        .matchFulfilled,
      (state, action: PayloadAction<Response<Country>>) => {
        state.countries =
          action.payload?.data?.edges?.map((item) => ({
            name: item.node.name,
            value: item.node.id,
          })) || [];
      },
    );
    builder.addMatcher(
      administrationDictionariesApi.endpoints.getActiveCurrencies
        .matchFulfilled,
      (state, action: PayloadAction<Response<Currency>>) => {
        state.activeCurrencies = action?.payload?.data?.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      administrationDictionariesApi.endpoints.getEmployeesDictionaries
        .matchFulfilled,
      (
        state,
        action: PayloadAction<
          Response<{
            id: string;
            employeeRoleTemplateId: string;
            status: string;
            profile: {
              firstName: string;
              lastName: string;
            };
          }>
        >,
      ) => {
        state.users =
          action.payload?.data?.edges?.map((item) => ({
            name: `${item.node.profile.firstName} ${item.node.profile.lastName}`,
            value: item.node.id,
          })) || [];

        state.activeUsers = action.payload?.data?.edges
          ?.filter((item) => item.node.status === 'ACTIVE')
          ?.map((item) => ({
            name: `${item.node.profile.firstName} ${item.node.profile.lastName}`,
            value: item.node.id,
          }));
      },
    );
    builder
      .addMatcher(
        administrationDictionariesApi.endpoints.getAdministrationsPermissions
          .matchFulfilled,
        (state, action: PayloadAction<{ data: PermissionResponse }>) => {
          state.activePermissions = action.payload.data.permissions;

          localStorage.setItem(
            'activePermissions',
            JSON.stringify(action.payload.data.permissions),
          );
        },
      )
      .addMatcher(
        administrationDictionariesApi.endpoints.getEmployeesWhitelabel
          .matchFulfilled,
        (
          state,
          action: PayloadAction<{
            data: {
              data: {
                value: Record<string, string>;
              };
            };
          }>,
        ) => {
          state.whitelabel = action.payload.data;
        },
      );
  },
});

export const {
  setActivePermissions,
  setCardsEnabled,
  setAdministrationCountries,
  setAdministrationCurrencies,
} = administrationDictionariesSlice.actions;

export default administrationDictionariesSlice.reducer;
